import { useState, useEffect, useRef } from 'react'
import Icon from '@algbra/ui/components/Icon/Icon'
import { Container } from '@algbra/ui/components/Grid/Grid'
import FreeText from '@algbra/ui/components/FreeText/FreeText'
import Input from '@algbra/ui/components/Input/Input'
import Whitespace from '@algbra/ui/components/Whitespace/Whitespace'
import { getIconByName } from '@algbra/ui/icons'
import { map, includes, filter } from 'rambdax'
import { nl2br } from '@algbra/ui/text'
import axios from 'axios'
import getSymbolFromCurrency from 'currency-symbol-map'
import CurrencyInput from 'react-currency-input-field'
import ReCAPTCHA from 'react-google-recaptcha'
import moment from 'moment'
import Config from '../../config'
import styles from './ExchangeRateCalculator.module.scss'
import Loading from '@algbra/ui/components/Loading/Loading'

export type ZoneExchangeRateCalculatorProps = {
  __typename: 'StrapiGQL_ComponentZonesExchangeRateCalculator'
  info: string
  disclaimer: string
}

export type MasterCardCurrency = {
  alphaCd: string
  currName: string
}

export type CalculationResult = {
  transCurr: string
  crdhldBillCurr: string
  rateDate: string
  transAmt: number
  bankFeePct: number
  bankFeeFixed: number
  mastercard: {
    mastercardFxRateDate: string
    mastercardConvRateInclPctFee: number
    mastercardConvRateExclAllFees: number
    crdhldBillAmtInclAllFees: number
    crdhldBillAmtExclAllFees: number
  }
  ecb: {
    ecbReferenceRateDate: string
    ecbReferenceRate: number
  }
  effectiveConversionRate: number
  pctDifferenceMastercardExclAllFeesAndEcb: number
  pctDifferenceMastercardInclAllFeesAndEcb: number
}

export function ZoneExchangeRateCalculator({
  info,
  disclaimer,
}: ZoneExchangeRateCalculatorProps) {
  const [rawCurrencies, setRawCurrencies] = useState<MasterCardCurrency[]>()
  const [exchange, setExchange] = useState({ From: 'GBP', To: 'EUR' })
  const [dropdown, setDropdown] = useState(false)
  const [field, setField] = useState('From')
  const [result, setResult] = useState<CalculationResult>()
  const [currencies, setCurrencies] = useState<MasterCardCurrency[]>()
  const popCurrencies = ['GBP', 'EUR', 'USD']
  const [amount, setAmount] = useState(1)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [isLoading, setLoading] = useState(false)
  const date = moment().format('YYYY-MM-DD')
  const [isCalculating, setCalculate] = useState(false)

  const excFrom = exchange.From
  const excTo = exchange.To
  const dropdownRef = useRef()

  useOnClickOutside(dropdownRef, () => setDropdown(false))

  function useOnClickOutside(ref, handler) {
    useEffect(() => {
      const listener = event => {
        if (!ref.current || ref.current.contains(event.target)) {
          return
        }
        handler(event)
      }
      document.addEventListener('mousedown', listener)
      document.addEventListener('touchstart', listener)
      return () => {
        document.removeEventListener('mousedown', listener)
        document.removeEventListener('touchstart', listener)
      }
    }, [ref, handler])
  }

  useEffect(() => {
    setLoading(true)
    axios.get('/.netlify/functions/mastercard-currencies').then(response => {
      setCurrencies(response.data.data.currencies)
      setRawCurrencies(response.data.data.currencies)
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (!rawCurrencies || !amount || !recaptchaRef.current) {
        return
      }

      setLoading(true)
      const captcha = recaptchaRef.current.executeAsync()
      captcha.then(token => {
        axios
          .post('/.netlify/functions/mastercard-calculator', {
            date,
            excFrom,
            excTo,
            amount,
            token,
          })
          .then(response => {
            if (recaptchaRef?.current) {
              recaptchaRef.current.reset()
            }

            setResult(response.data.data)
            setLoading(false)
          })
          .catch(e => {
            if (recaptchaRef?.current) {
              recaptchaRef.current.reset()
            }
            setLoading(false)
            window.alert(
              'There was an error trying to send your request. Please try again later.'
            )
          })
      })
    }, 750)

    return () => clearTimeout(delayDebounceFn)
  }, [amount, date, excFrom, excTo, rawCurrencies])

  const selectField = wField => {
    setDropdown(!dropdown)
    setField(wField)
  }

  const selectCurrency = curr => {
    setDropdown(!dropdown)
    setExchange({ ...exchange, [field]: curr })
    setCurrencies(rawCurrencies)
  }

  const search = value => {
    setCurrencies(
      filter(
        x =>
          includes(value.toLowerCase(), x.alphaCd.toLowerCase()) ||
          includes(value.toLowerCase(), x.currName.toLowerCase()),
        rawCurrencies
      )
    )
  }

  const SelectItem = props => {
    return (
      <button
        className={styles.dropdownItem}
        onClick={() => selectCurrency(props.currencyCode)}
      >
        <span className={styles.countryFlag}>
          <img
            src={`/images/flags/${props.currencyCode}.svg`}
            alt={props.currencyCode}
          />
        </span>
        <span>
          <span className={styles.currencyCode}>{props.currencyCode}</span>
          <span className={styles.currencyName}>{props.currencyName}</span>
        </span>
      </button>
    )
  }

  return (
    <Whitespace type="none">
      <Container>
        <ReCAPTCHA
          size="invisible"
          ref={recaptchaRef}
          sitekey={Config.reCaptchaKey}
        />

        <div className={styles.container}>
          {dropdown && (
            <div ref={dropdownRef} className={styles.exchangeDropdown} style={{top: field === 'From' ? '0' : '102px'}}>
              <div className={styles.inputWrapper}>
                <Icon
                  className={styles.searchIcon}
                  icon={getIconByName('search')}
                  size={24}
                />
                <Input
                  name="search"
                  type="text"
                  className={styles.searchInputContainer}
                  controlClassName={styles.searchInput}
                  placeholder="Search currency or country"
                  onChange={e => search(e.currentTarget.value)}
                />
              </div>

              <div className={styles.innerContent}>
                {filter(x => includes(x.alphaCd, popCurrencies), currencies)
                  .length ? (
                  <label className={styles.label}>Popular Currencies</label>
                ) : (
                  ''
                )}
                {currencies.length
                  ? map(
                      ({ currName, alphaCd }) => (
                        <SelectItem
                          key={alphaCd}
                          currencyCode={alphaCd}
                          currencyName={currName}
                        ></SelectItem>
                      ),
                      filter(
                        x =>
                          includes(x.alphaCd, popCurrencies) &&
                          x.alphaCd !==
                            (field === 'From' ? exchange.To : exchange.From),
                        currencies
                      ) || []
                    )
                  : ''}

                {currencies.length ? (
                  <label className={styles.label}>All Currencies</label>
                ) : (
                  ''
                )}

                {currencies.length ? (
                  map(
                    ({ currName, alphaCd }) => (
                      <SelectItem
                        key={alphaCd}
                        currencyCode={alphaCd}
                        currencyName={currName}
                      ></SelectItem>
                    ),
                    filter(
                      x =>
                        x.alphaCd !==
                        (field === 'From' ? exchange.To : exchange.From),
                      currencies
                    ) || []
                  )
                ) : (
                  <FreeText className={styles.noResult}>No Result</FreeText>
                )}
              </div>
            </div>
          )}

          <div className={styles.exchangeItem}>
            {isLoading && (
              <div className={styles.loading}>
                <Loading />
              </div>
            )}
            <div>
              <label className={styles.label}>Amount</label>
              <div className={styles.currInpWrapper}>
                <span className={styles.currSymbol}>
                  {getSymbolFromCurrency(exchange.From)}
                </span>
                <CurrencyInput
                  defaultValue={1}
                  decimalsLimit={2}
                  maxLength={7}
                  className={styles.exchangeInput}
                  onValueChange={(value, name) => setAmount(value)}
                />
              </div>
            </div>
            <div>
              <button
                onClick={() => selectField('From')}
                className={styles.exchangeSelect}
              >
                <span className={styles.flag}>
                  <img
                    src={`/images/flags/${exchange.From}.svg`}
                    alt={exchange.From}
                  />
                </span>
                <span className={styles.text}>{exchange.From}</span>
                <Icon icon={getIconByName('arrow-down')} size={11} />
              </button>
            </div>
          </div>

          <div className={styles.exchangeItem}>
            {isLoading && (
              <div className={styles.loading}>
                <Loading />
              </div>
            )}
            <div>
              <label className={styles.label}>Converted To</label>
              <div className={styles.currInpWrapper}>
                <span className={styles.currSymbol}>
                  {getSymbolFromCurrency(exchange.To)}
                </span>

                <CurrencyInput
                  disabled
                  value={result && result.mastercard.crdhldBillAmtInclAllFees}
                  decimalsLimit={2}
                  className={styles.exchangeInput}
                />
              </div>
            </div>
            <div>
              <button
                onClick={() => selectField('To')}
                className={styles.exchangeSelect}
              >
                <span className={styles.flag}>
                  <img
                    src={`/images/flags/${exchange.To}.svg`}
                    alt={exchange.To}
                  />
                </span>
                <span className={styles.text}>{exchange.To}</span>
                <Icon icon={getIconByName('arrow-down')} size={11} />
              </button>
            </div>
          </div>

          {result && (
            <div className={styles.pr}>
              {isLoading && (
                <div className={styles.loading}>
                  <Loading />
                </div>
              )}
              <div className={styles.resultItem}>
                <FreeText type="md" className={styles.resText}>
                  ECB Rate
                  <span>
                    Updated on{' '}
                    {moment(result.ecb.ecbReferenceRateDate).format(
                      'D MMMM YYYY'
                    )}
                  </span>
                </FreeText>

                {result.ecb.ecbReferenceRate ? (
                  <FreeText type="md" className={styles.resText}>
                    {getSymbolFromCurrency(result.transCurr)}1 ={' '}
                    {getSymbolFromCurrency(result.crdhldBillCurr)}
                    {result.ecb.ecbReferenceRate}
                  </FreeText>
                ) : (
                  '-'
                )}
              </div>

              <div className={styles.resultItem}>
                <FreeText type="md" className={styles.resText}>
                  Mastercard Rate
                  <span>
                    Updated on{' '}
                    {moment(result.mastercard.mastercardFxRateDate).format(
                      'D MMMM YYYY'
                    )}
                  </span>
                </FreeText>

                <FreeText type="md" className={styles.resText}>
                  {getSymbolFromCurrency(result.transCurr)}1 ={' '}
                  {getSymbolFromCurrency(result.crdhldBillCurr)}
                  {result.mastercard.mastercardConvRateInclPctFee}
                </FreeText>
              </div>

              <div className={styles.resultItem}>
                <FreeText type="md" className={styles.resText}>
                  Our Fee Excluded
                </FreeText>

                <FreeText type="md" className={styles.resText}>
                  {result.pctDifferenceMastercardExclAllFeesAndEcb ? result.pctDifferenceMastercardExclAllFeesAndEcb + '%' : "-"}
                </FreeText>
              </div>

              <div className={styles.resultItem}>
                <FreeText type="md" className={styles.resText}>
                  Our Fee Included
                </FreeText>

                <FreeText type="md" className={styles.resText}>
                  {result.pctDifferenceMastercardInclAllFeesAndEcb ? result.pctDifferenceMastercardInclAllFeesAndEcb + '%' : '-'}
                </FreeText>
              </div>

              <div className={styles.resultItem}>
                <FreeText type="md" className={styles.resText}>
                  Total mark-up over ECB Rate
                </FreeText>

                <FreeText type="md" className={styles.resText}>
                  {result.pctDifferenceMastercardInclAllFeesAndEcb ? result.pctDifferenceMastercardInclAllFeesAndEcb + '%' : '-'}
                </FreeText>
              </div>
            </div>
          )}

          <div className={styles.info}>
            <span className={styles.infoIcon}>
              <Icon icon={getIconByName('info')} size={15} />
            </span>
            <p>{info}</p>
          </div>
        </div>

        <div
          className={styles.disclaimer}
          dangerouslySetInnerHTML={{ __html: nl2br(disclaimer) }}
        ></div>
      </Container>
    </Whitespace>
  )
}
